import React, { useState, useCallback, useMemo } from 'react';
import {
  Grid,
  Typography,
  Divider,
  Tooltip,
  Box,
  Fab,
} from '@material-ui/core';
import { Controller, useFormContext } from 'react-hook-form';
import { Column } from 'react-table';
import { orange } from '@material-ui/core/colors';
import PersonIcon from '@material-ui/icons/Person';
import styled from 'styled-components';
import { DealDetail, EntityType } from '@lib/deals';
import { DraggableRowsTable } from '@components/draggable-rows-table';
import { EntitySearchMultiParty } from '@components/entity-search/entity-seach-multi-party';
import { C3AddFormEntity } from './types';

export const ScrollSectionTitle = ({ children, icon, subTitle, id }: {
  children: React.ReactNode;
  icon?: React.ReactNode;
  subTitle?: string;
  id?: string;
}) => (
  <Grid container id={id} spacing={icon ? 2 : 0} alignItems="center">
    {icon && <Grid item>{icon}</Grid>}
    <Grid item>
      <Typography variant="h5">{children}</Typography>
      {subTitle && (
        <Typography color="textSecondary" variant="body2">
          {subTitle}
        </Typography>
      )}
    </Grid>
  </Grid>
);

interface C3PersonSelectorProps {
  deal: C3AddFormEntity;
  createDeal?: boolean;
}
export const CONTENT_TO_PERSON = "content2person";

export function C3PersonSelector({ deal, createDeal = false }: C3PersonSelectorProps) {
  const form = useFormContext();

  const [indexesPerson, setIndexesPerson] = useState(
    Array.from(Array(deal?.associated_person?.length || 1), (_, i) => i)
  );
  
  const removePersons = useCallback((index: number) => {
    setIndexesPerson(prevIndexes => {
      return [...prevIndexes.filter(item => item !== index)];
    });
    const watchOnPersons = form.watch('associated_person');
    watchOnPersons
      ?.filter((x: any, idx: number) => idx !== index)
      .forEach((eachRow: any, idx: number) => {
        if (eachRow) eachRow['order_id'] = idx + 1;
      });
  }, []);
  
  const onChangeDraggedRows = (allRowsModified: any[]) => {
    setIndexesPerson(allRowsModified.map(eachRow => eachRow.id));
  };
  
  const getRowId = React.useCallback((row: any, idx: number) => {
    return '' + row.id;
  }, []);
  const columns: Column<any>[] = useMemo(
    () => [
      {
        Header: 'Cell',
        id: 'cell',
        Cell: ({ row, idx }) => {
          const isRowDeletetable = row.original.deleteable;
          const index = row.original.id;
          const fieldName = `associated_person[${index}]`;
   
          return (
            <Controller
              name={fieldName}
              key={index}
              control={form.control}
              defaultValue={
                deal?.associated_person?.[index]
                  ? deal.associated_person[index]
                  : {
                      name: '',
                      type: EntityType.person,
                      vtr_id: '',
                      meta: null,
                      relation_type: 'person',
                      order_id: index + 1,
                    }
              }
              render={({ onChange }) => {
                const defaultValue:any = deal?.associated_person?.[index]
                  ? deal.associated_person[index]
                  : {
                      name: '',
                      type: EntityType.person,
                      vtr_id: '',
                      meta: null,
                      relation_type: 'person',
                      order_id: index + 1,
                    };
                
                return (
                  <PaddingBottomEntitySearch key={index}>
                    <EntitySearchMultiParty
                      label="Person"
                      fixedType={EntityType.person}
                      defaultValue={defaultValue}
                      hideTypeSelect
                      clearable
                      onChange={(value) => {
                        onChange(value);
                      }}
                      titleEditable
                      indexOfEntities={index}
                      removeDealEntities={removePersons}
                      isDealEntityRemovable={isRowDeletetable}
                      dealData={form.getValues()}
                      isDealEntityRequestCreatable={!createDeal}
                      disabled={false}
                      defaultSearchIn="name"
                      stopInitialCall={false}
                      companyPublishable={false}
                      page={CONTENT_TO_PERSON}
                    />
                  </PaddingBottomEntitySearch>
                );
              }}
            />
          );
        },
      },
    ],
    [deal?.associated_person, removePersons, createDeal]
  );
  

  
  return (
    <React.Fragment key="persons">
      <Grid container justify="space-between" style={{ marginRight: '30px' }}>
        <Grid item>
          <ScrollSectionTitle
            id="c3-persons"
            subTitle={'Associated persons in C3 Relation'}
            icon={<PersonIcon fontSize="large" style={{ color: orange[700] }} />}>
            Select Person
          </ScrollSectionTitle>
        </Grid>
      {/*   <Grid item style={{ paddingRight: '20px', paddingBottom: '10px' }}>
          <Tooltip title="Add Person">
            <StyledFab onClick={addPersons} color="primary" aria-label="add" size="small">
              <AddIcon />
            </StyledFab>
          </Tooltip>
        </Grid> */}
      </Grid>
      <Divider />
      <br />
      <fieldset style={{ padding: '0.75em', backgroundColor: '#f5f5f5', border: 'none' }}>
        <DraggableRowsTableStyled
          getRowId={getRowId}
          onChange={onChangeDraggedRows}
          columns={columns}
          data={indexesPerson.map(idx => ({
            id: idx,
            deleteable: indexesPerson.length > 1,
          }))}
          stickyHeader
          size="small"
          hideHeaders
          showDragIcon={false}
          multiSectionName={EntityType.person}
        />
      </fieldset>
    </React.Fragment>
  );
}

const DraggableRowsTableStyled = styled(DraggableRowsTable)`
  & .MuiTableCell-root.MuiTableCell-body.MuiTableCell-sizeSmall {
    padding-bottom: 10px;
    padding-top: 10px;
  }
`;

const StyledFab = styled(Fab)`
  &.MuiFab-sizeSmall {
    width: 40px;
  }
  & .MuiSvgIcon-root {
    font-size: 1.4rem;
  }
`;

const PaddingBottomEntitySearch = styled.div`
  margin-bottom: 20px;
  :last-child {
    margin-bottom: 0px;
  }
`; 